import type {
	ProductionQuestion,
	ProductionQuestionAttachment,
	ProductionQuestionLineItem,
} from '../../../interfaces/ProductionQuestion.interface';
import { formatUserWithTimestamp } from './formatters';

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function getLineItems(record: ProductionQuestion): ProductionQuestionLineItem[] {
	if (record.order_line_items?.length > 0) {
		return record.order_line_items;
	}
	if (record.order_line_item_id) {
		return [
			{
				id: record.order_line_item_id,
				order_name: record.order_name,
				line_item_name: record.line_item_name,
			},
		];
	}
	return [];
}

async function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

async function resolveImageSrc(url: string): Promise<string> {
	try {
		const response = await fetch(url);
		if (!response.ok) {
			return url;
		}
		const blob = await response.blob();
		return blobToDataUrl(blob);
	} catch {
		return url;
	}
}

async function buildImageSection(
	attachments: ProductionQuestionAttachment[],
	sectionLabel: string
): Promise<{ html: string; text: string }> {
	if (!attachments.length) {
		return { html: '', text: '' };
	}

	const resolved = await Promise.all(
		attachments.map(async (attachment, index) => {
			const url = attachment.url;
			if (!url) {
				return null;
			}
			const src = await resolveImageSrc(url);
			const alt = `${sectionLabel} ${index + 1}`;
			return { src, alt, url };
		})
	);

	const images = resolved.filter(
		(item): item is { src: string; alt: string; url: string } => item !== null
	);

	if (!images.length) {
		return { html: '', text: '' };
	}

	const html = [
		`<p><strong>${escapeHtml(sectionLabel)}</strong></p>`,
		...images.map(
			({ src, alt }) =>
				`<p><img src="${src}" alt="${escapeHtml(alt)}" style="max-width:600px;height:auto;display:block;margin:8px 0;" /></p>`
		),
	].join('\n');

	const text = [
		sectionLabel,
		...images.map(({ url }, index) => `  ${index + 1}. ${url}`),
	].join('\n');

	return { html, text };
}

/**
 * Copies the reported question (metadata, description, screenshots) as rich
 * HTML plus a plain-text fallback for pasting into email clients.
 */
export async function copyProductionQuestionReportToClipboard(
	record: ProductionQuestion
): Promise<void> {
	const lineItems = getLineItems(record);
	const lineItemsText = lineItems
		.map((item) => `${item.order_name} — ${item.line_item_name}`)
		.join('\n');

	const textFields: { label: string; value: string }[] = [
		{ label: 'Question ID', value: record.uid },
		{ label: 'Category', value: record.category_display },
		{
			label: 'Reported By',
			value: formatUserWithTimestamp(record.reported_by, record.created_time),
		},
	];

	if (lineItems.length) {
		textFields.push({ label: 'Line Items', value: lineItemsText });
	}

	if (record.description) {
		textFields.push({ label: 'Description', value: record.description });
	}

	const [drawingSection, modelSection] = await Promise.all([
		buildImageSection(record.drawing_attachments ?? [], 'Drawing Screenshots'),
		buildImageSection(record.model_attachments ?? [], '3D Model Screenshots'),
	]);

	const plainSections = [
		'Production Question Report',
		'==========================',
		'',
		...textFields.flatMap(({ label, value }) => [`${label}:`, value, '']),
	];

	if (drawingSection.text) {
		plainSections.push(drawingSection.text, '');
	}
	if (modelSection.text) {
		plainSections.push(modelSection.text, '');
	}

	const plainText = plainSections.join('\n').trimEnd();

	const htmlFields = textFields
		.map(
			({ label, value }) =>
				`<p><strong>${escapeHtml(label)}:</strong><br/>${escapeHtml(value).replace(/\n/g, '<br/>')}</p>`
		)
		.join('\n');

	const html = [
		'<div>',
		'<p><strong>Production Question Report</strong></p>',
		htmlFields,
		drawingSection.html,
		modelSection.html,
		'</div>',
	].join('\n');

	const htmlBlob = new Blob([html], { type: 'text/html' });
	const textBlob = new Blob([plainText], { type: 'text/plain' });

	await navigator.clipboard.write([
		new ClipboardItem({
			'text/html': htmlBlob,
			'text/plain': textBlob,
		}),
	]);
}
